import { useEffect, useRef, useState } from 'react';
import { getImagePath } from '../utils/imagePath';
import EditableText from './EditableText';

const Pricing = () => {
  const subtitleRef = useRef(null);
  const sectionRef = useRef(null);
  const [showVideoVersion, setShowVideoVersion] = useState(true);
  const [videoMask, setVideoMask] = useState('radial-gradient(ellipse 90% 90% at center, black 70%, transparent 100%)');

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const updateMask = () => {
      setVideoMask(
        mql.matches
          ? 'radial-gradient(ellipse 80% 90% at center, black 70%, transparent 100%)'
          : 'radial-gradient(ellipse 90% 90% at center, black 70%, transparent 100%)'
      );
    };
    updateMask();
    mql.addEventListener('change', updateMask);
    return () => mql.removeEventListener('change', updateMask);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && subtitleRef.current) {
            subtitleRef.current.classList.add('slide-in-from-right');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    const sectionElement = sectionRef.current;
    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, []);

  // Hotkey listener for 'p' key
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only trigger if not typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      if (e.key === 'p' || e.key === 'P') {
        setShowVideoVersion(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);



  const handleGetQuote = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      const navbar = document.querySelector('[data-navbar]');
      const navbarHeight = navbar ? navbar.offsetHeight : 140;
      const targetPosition = contactSection.offsetTop - navbarHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      ref={sectionRef}
      id="pricing"
      className="py-0 pb-10 md:pb-0 md:py-12 lg:py-16 px-0 md:px-4 sm:px-6 lg:px-8 m-0 bg-white relative overflow-visible z-[10] w-full max-w-[100vw]"
    >
      <div className="!max-w-[1188px] mx-auto w-full relative overflow-visible z-[1] relative">
        {/* Section Title - on mobile: smaller, no wrap; visible above video */}
        <h2 className="block text-center text-lg md:text-4xl lg:text-5xl mb-3 md:mb-6 lg:mb-8 text-black relative w-full font-semibold uppercase tracking-[2px] md:tracking-[4px] font-serif border-b-0 pb-4 pt-6 px-4 md:pt-0 md:px-0 whitespace-nowrap md:whitespace-normal after:content-[''] after:absolute after:-bottom-[10px] after:left-1/2 after:-translate-x-1/2 after:w-[150px] sm:after:w-[200px] md:after:w-[250px] after:h-[4px] sm:after:h-[5px] md:after:h-[6px] after:bg-[var(--accent-color)]">
          <EditableText path="pricing.sectionTitle" fallback="Pricing" /> <span ref={subtitleRef} className="inline-block pricing-subtitle"><EditableText path="pricing.subtitle" fallback="That Fits Your Needs" /></span>
        </h2>

        {/* Pricing Intro - visible on mobile above video, and on desktop */}
        <div className="block max-w-[900px] mx-auto mb-4 md:mb-8 text-center px-4 md:px-0">
          <p className="text-base md:text-xl leading-relaxed text-[var(--text-dark)] font-serif">
            <EditableText path="pricing.intro" fallback="Every organization has unique goals, audiences, and requirements. That's why we take a customized approach to pricing—rather than offering a one-size-fits-all package. Our team works closely with you to design a solution that aligns with your objectives, timeline, and budget." />
          </p>
        </div>

        {/* Pricing Content with Video or Quote Card - on mobile: overflow-visible so paragraph card isn't clipped */}
        <div className="flex flex-col lg:flex-row gap-0 md:gap-4 lg:gap-6 items-start lg:items-center mb-4 md:mb-6 relative z-[2] overflow-visible min-h-0">
          {/* Video or Quote Card - Left Side */}
          <div className="w-full lg:w-[500px] lg:flex-shrink-0 relative rounded-lg overflow-hidden flex justify-center md:block">
            {showVideoVersion ? (
              <>
                <div className="relative overflow-hidden rounded-lg w-[92%] max-w-[92vw] md:w-full md:max-w-none mx-auto md:mx-0" style={{
                  maskImage: videoMask,
                  WebkitMaskImage: videoMask,
                }}>
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto object-contain object-center md:object-cover md:h-auto md:max-w-[600px] md:scale-110"
                    style={{ objectPosition: 'center' }}
                  >
                    <source src={getImagePath("/videos/loop-reverse.mp4")} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                {/* Soft edge overlay - stronger blend on top */}
                <div className="absolute inset-0 pointer-events-none rounded-lg" style={{
                  background: 'radial-gradient(ellipse 85% 75% at 50% 45%, transparent 60%, rgba(250, 250, 250, 0.4) 75%, rgba(250, 250, 250, 0.7) 90%, rgba(250, 250, 250, 0.95) 100%)',
                  mixBlendMode: 'multiply'
                }}></div>
              </>
            ) : (
              <div className="w-full max-w-[600px] bg-[#9a83ad] p-8 md:p-12 rounded-[15px] shadow-[0_4px_12px_rgba(154,131,173,0.2),0_8px_24px_rgba(154,131,173,0.15)] flex flex-col items-center justify-center gap-5 text-center">
                <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-semibold m-0 font-serif uppercase tracking-[2px]">
                  <EditableText path="pricing.getQuoteTitle" fallback="Get Your Quote Today" />
                </h3>
                <button
                  onClick={handleGetQuote}
                  className="inline-block py-3.5 px-9 bg-black text-white no-underline text-[0.95rem] font-semibold uppercase tracking-[1.5px] rounded cursor-pointer transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] shadow-none font-serif border-2 border-black relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:text-white before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-[#9a83ad] before:transition-[left] before:duration-[500ms] before:ease-[cubic-bezier(0.4,0,0.2,1)] before:-z-10 hover:before:left-0 active:translate-y-0"
                >
                  <EditableText path="pricing.getQuoteCta" fallback="Contact Us" tag="span" />
                </button>
              </div>
            )}
          </div>

          {/* Main Pricing Summary Card - on mobile: below video, full height (no flex shrink); on desktop: right of video */}
          <div className="block flex-1 min-w-0 bg-[var(--bg-dark)] p-5 md:p-12 rounded-xl shadow-md border border-[var(--border-dark)] relative z-[1] mt-4 md:mt-0 mx-4 md:mx-0 overflow-visible shrink-0 md:shrink">
            <p className="text-[var(--bg-pure-white)] leading-relaxed text-sm md:text-lg mb-4 md:mb-6 font-sans">
            <EditableText path="pricing.summaryPara1" fallback="Every organization has unique goals, audiences, and requirements. That's why we take a customized approach to pricing—rather than offering a one-size-fits-all package. Our team works closely with you to design a solution that aligns with your objectives, timeline, and budget." />
          </p>
          <p className="text-[var(--bg-pure-white)] leading-relaxed text-sm md:text-lg mb-4 md:mb-6 font-sans">
            <EditableText path="pricing.summaryPara2" fallback="We work closely with agencies, consultants, and resellers, we do not publish standard pricing. Each engagement is priced individually based on factors such as campaign scope, technical requirements, and messaging volume. This approach allows us to deliver the right solution—without unnecessary costs or compromises." />
          </p>
          <p className="text-[var(--bg-pure-white)] leading-relaxed text-sm md:text-lg mb-0 font-sans">
            Thank you for considering{' '}
            <span className="text-[#9a83ad] font-semibold">
              <EditableText path="pricing.companyName" fallback="The VBC Group" />
            </span>
            . We look forward to partnering with you to achieve your goals and deliver{' '}
            <span className="text-[#9a83ad] font-semibold">
              <EditableText path="pricing.resultsTrust" fallback="results you can trust" />
            </span>
            .
          </p>
          </div>
        </div>

        {/* Get Your Quote Today Card - on mobile: constrained to screen width; extra space below */}
        {showVideoVersion && (
          <div className="flex w-[92%] max-w-[calc(100vw-2rem)] md:w-full md:max-w-[850px] mx-auto mt-4 md:mt-8 lg:mt-10 mb-10 md:mb-0 bg-[#9a83ad] p-6 md:p-12 rounded-[15px] shadow-[0_4px_12px_rgba(154,131,173,0.2),0_8px_24px_rgba(154,131,173,0.15)] flex-col items-center justify-center gap-5 text-center relative z-[1]">
            <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-semibold m-0 font-serif uppercase tracking-[2px]">
              <EditableText path="pricing.getQuoteTitle" fallback="Get Your Quote Today" />
            </h3>
            <button
              onClick={handleGetQuote}
              className="inline-block py-3.5 px-9 bg-black text-white no-underline text-[0.95rem] font-semibold uppercase tracking-[1.5px] rounded cursor-pointer transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] shadow-none font-serif border-2 border-black relative overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:text-white before:content-[''] before:absolute before:top-0 before:-left-full before:w-full before:h-full before:bg-[#9a83ad] before:transition-[left] before:duration-[500ms] before:ease-[cubic-bezier(0.4,0,0.2,1)] before:-z-10 hover:before:left-0 active:translate-y-0"
            >
              <EditableText path="pricing.getQuoteCta" fallback="Contact Us" tag="span" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Pricing;
